/**
 * CampaignsManagement - Admin component for managing donation campaigns
 */
import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Eye, ExternalLink, Upload, Loader2, X,
  Heart, Target, Image, FileText, Users, DollarSign, TrendingUp,
  CheckCircle, Clock, AlertCircle, Mail, Phone, MapPin, Settings, Copy, Download,
  Sparkles, Building2, Church, Plane, GraduationCap, Users2, CreditCard, Calendar, QrCode, Repeat
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '@/lib/api';
import PledgesManagement from './PledgesManagement';
import CampaignProgressTracker from './CampaignProgressTracker';
import PeriodTargets from './PeriodTargets';
import CampaignReachPanel from '@/components/campaigns/CampaignReachPanel';
import CampaignSettings from './CampaignSettings';
import WorkflowChecklist from '@/components/workflow/WorkflowChecklist';
import { getToken } from '@/lib/authToken';
import { MediaUpload } from '@/components/MediaUpload';
import CurrencySelect from '@/components/CurrencySelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Template icons mapping
const TEMPLATE_ICONS = {
  health_center: Building2,
  church_building: Church,
  missions_trip: Plane,
  community_project: Users2,
  school_education: GraduationCap,
  blank: FileText
};

const TEMPLATE_COLORS = {
  health_center: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  church_building: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  missions_trip: 'bg-amber-100 text-amber-700 border-amber-200',
  community_project: 'bg-purple-100 text-purple-700 border-purple-200',
  school_education: 'bg-blue-100 text-blue-700 border-blue-200',
  blank: 'bg-slate-100 text-slate-700 border-slate-200'
};

const CampaignsManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrCampaign, setQRCampaign] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({ name: '', slug: '', goal_amount: '' });
  const [cloningCampaign, setCloningCampaign] = useState(null);
  const [cloneFormData, setCloneFormData] = useState({ name: '', slug: '' });
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState('campaigns');
  const fileInputRef = useRef(null);
  const qrRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    tagline: '',
    description: '',
    mission_statement: '',
    target_amount: '',
    currency: 'USD',
    allowed_currencies: 'USD',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    hero_image: '',
    logo: ''
  });

  // Fetch campaigns
  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['admin-campaigns'],
    queryFn: async () => {
      const res = await api.get('/campaigns/admin/list');
      return res.data;
    }
  });

  // Fetch templates
  const { data: templates = [] } = useQuery({
    queryKey: ['campaign-templates'],
    queryFn: async () => {
      const res = await api.get('/campaigns/templates');
      return res.data;
    }
  });

  // Create/Update campaign
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      const formDataObj = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          formDataObj.append(key, value);
        }
      });

      // Debug logging
      console.log('Campaign form data being sent:', Object.fromEntries(formDataObj.entries()));

      // Use fetch instead of axios to properly handle FormData content-type
      const token = getToken() || 
        getToken();
      
      const url = editingCampaign 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/admin/${editingCampaign.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/admin/create`;
      
      const response = await fetch(url, {
        method: editingCampaign ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: formDataObj
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw { response: { data: errorData } };
      }
      
      return { data: await response.json() };
    },
    onSuccess: () => {
      toast.success(editingCampaign ? 'Campaign updated!' : 'Campaign created!');
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
      closeDialog();
    },
    onError: (error) => {
      console.error('Campaign save error:', error.response?.data);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        // Validation errors from FastAPI
        const messages = detail.map(e => `${e.loc?.join('.') || 'Field'}: ${e.msg || e.message || 'Invalid'}`).join(', ');
        toast.error(messages || 'Validation error');
      } else {
        toast.error('Failed to save campaign');
      }
    }
  });

  // Create from template
  const createFromTemplateMutation = useMutation({
    mutationFn: async ({ templateId, name, slug, goal_amount }) => {
      const formDataObj = new FormData();
      formDataObj.append('template_id', templateId);
      formDataObj.append('name', name);
      formDataObj.append('slug', slug);
      if (goal_amount) formDataObj.append('goal_amount', goal_amount);
      return api.post('/campaigns/admin/from-template', formDataObj);
    },
    onSuccess: (response) => {
      const data = response.data;
      toast.success(`Campaign created from template! ${data.milestones_created} milestones added.`);
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
      setShowTemplateDialog(false);
      setSelectedTemplate(null);
      setTemplateFormData({ name: '', slug: '', goal_amount: '' });
    },
    onError: (error) => {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        const messages = detail.map(e => e.msg || e.message || 'Error').join(', ');
        toast.error(messages);
      } else {
        toast.error('Failed to create campaign');
      }
    }
  });

  // Delete campaign
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return api.delete(`/campaigns/admin/${id}`);
    },
    onSuccess: () => {
      toast.success('Campaign deleted');
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
    },
    onError: () => {
      toast.error('Failed to delete campaign');
    }
  });

  // Toggle publish status
  const togglePublishMutation = useMutation({
    mutationFn: async ({ id, is_published }) => {
      const formDataObj = new FormData();
      formDataObj.append('is_published', String(is_published));
      return api.put(`/campaigns/admin/${id}`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    onSuccess: () => {
      toast.success('Campaign status updated');
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
    },
    onError: (err) => {
      toast.error('Failed to update campaign status');
    }
  });

  // Toggle campaign visibility (public/private)
  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ id, visibility }) => {
      return api.put(`/campaigns/admin/${id}/visibility`, { visibility });
    },
    onSuccess: () => {
      toast.success('Campaign visibility updated');
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
    },
    onError: () => {
      toast.error('Failed to update visibility');
    }
  });

  // Clone campaign
  const cloneMutation = useMutation({
    mutationFn: async ({ campaignId, name, slug }) => {
      const formDataObj = new FormData();
      formDataObj.append('new_name', name);
      formDataObj.append('new_slug', slug);
      return api.post(`/campaigns/admin/${campaignId}/clone`, formDataObj);
    },
    onSuccess: (response) => {
      const data = response.data;
      toast.success(`Campaign cloned! ${data.cloned_items?.milestones || 0} milestones and ${data.cloned_items?.gallery_images || 0} images copied.`);
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
      setShowCloneDialog(false);
      setCloningCampaign(null);
      setCloneFormData({ name: '', slug: '' });
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to clone campaign');
    }
  });

  // Export campaign
  const handleExport = async (campaign) => {
    try {
      const res = await api.get(`/campaigns/admin/${campaign.id}/export`);
      const dataStr = JSON.stringify(res.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `campaign-${campaign.slug}-export.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Campaign exported!');
    } catch (error) {
      toast.error('Failed to export campaign');
    }
  };

  // Open clone dialog
  const openCloneDialog = (campaign) => {
    setCloningCampaign(campaign);
    setCloneFormData({
      name: `${campaign.name} (Copy)`,
      slug: `${campaign.slug}-copy`
    });
    setShowCloneDialog(true);
  };

  // Handle clone submit
  const handleCloneSubmit = (e) => {
    e.preventDefault();
    if (!cloneFormData.name || !cloneFormData.slug) {
      toast.error('Name and slug are required');
      return;
    }
    cloneMutation.mutate({
      campaignId: cloningCampaign.id,
      name: cloneFormData.name,
      slug: cloneFormData.slug
    });
  };

  // Upload file
  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      // Add file_type based on field
      const fileType = field === 'hero_image' ? 'hero' : field === 'logo' ? 'logo' : 'general';
      uploadData.append('file_type', fileType);
      
      // Use headers without Content-Type to let axios set it automatically for FormData
      const res = await api.post('/campaigns/admin/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, [field]: res.data.url });
      toast.success('File uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    }
    setUploading(false);
  };

  const openCreateDialog = () => {
    setEditingCampaign(null);
    setFormData({
      name: '',
      slug: '',
      tagline: '',
      description: '',
      mission_statement: '',
      target_amount: '',
      currency: 'USD',
      allowed_currencies: 'USD',
      contact_email: '',
      contact_phone: '',
      contact_address: '',
      hero_image: '',
      logo: ''
    });
    setShowDialog(true);
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name || '',
      slug: campaign.slug || '',
      tagline: campaign.tagline || '',
      description: campaign.description || '',
      mission_statement: campaign.mission_statement || '',
      target_amount: campaign.target_amount || '',
      currency: campaign.currency || 'USD',
      allowed_currencies: (campaign.allowed_currencies || [campaign.currency || 'USD']).join(','),
      contact_email: campaign.contact_email || '',
      contact_phone: campaign.contact_phone || '',
      contact_address: campaign.contact_address || '',
      hero_image: campaign.hero_image || '',
      logo: campaign.logo || ''
    });
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingCampaign(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.slug) {
      toast.error('Name and slug are required');
      return;
    }
    saveMutation.mutate(formData);
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Donation Campaigns</h2>
          <p className="text-slate-500">Manage fundraising campaigns for institutional projects</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowTemplateDialog(true)} 
            variant="outline"
            className="border-purple-200 text-purple-700 hover:bg-purple-50"
            data-testid="new-from-template-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            From Template
          </Button>
          <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Heart className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{campaigns.length}</p>
                <p className="text-xs text-slate-500">Total Campaigns</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{campaigns.filter(c => c.is_published).length}</p>
                <p className="text-xs text-slate-500">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  ${campaigns.reduce((sum, c) => sum + (c.raised_amount || 0), 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Total Raised</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Target className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  ${campaigns.reduce((sum, c) => sum + (c.target_amount || 0), 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Total Goal</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Show Pledges or Targets for selected campaign */}
      {selectedCampaign && (activeTab === 'pledges' || activeTab === 'targets' || activeTab === 'reach') && (
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  {activeTab === 'pledges' ? 'Pledges'
                    : activeTab === 'reach' ? 'Who this appeal goes to'
                    : 'Period Targets'} - {selectedCampaign.name}
                </CardTitle>
                <CardDescription>
                  {activeTab === 'pledges'
                    ? 'Manage donor pledges and track payments'
                    : activeTab === 'reach'
                    ? 'Tick the assemblies taking part, set each one a goal, and see how they stand'
                    : 'Set weekly/monthly fundraising goals'}
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setSelectedCampaign(null);
                  setActiveTab('campaigns');
                }}
              >
                ← Back to Campaigns
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {activeTab === 'pledges' ? (
              <>
                <CampaignProgressTracker slug={selectedCampaign.slug} />
                <div className="mt-6">
                  <PledgesManagement 
                    campaignId={selectedCampaign.id} 
                    campaignName={selectedCampaign.name} 
                    campaignCurrency={selectedCampaign.currency || 'USD'}
                  />
                </div>
              </>
            ) : activeTab === 'reach' ? (
              <CampaignReachPanel
                campaignId={selectedCampaign.id}
                currency={selectedCampaign.currency || 'USD'}
              />
            ) : (
              <PeriodTargets campaignId={selectedCampaign.id} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Campaign Settings Dialog */}
      {selectedCampaign && activeTab === 'settings' && (
        <>
          <CampaignReadinessChecklist
            campaignId={selectedCampaign.id}
            tenantId={selectedCampaign.tenant_id || selectedCampaign.id}
          />
          <CampaignSettings
            campaign={selectedCampaign}
            onClose={() => {
              setSelectedCampaign(null);
              setActiveTab('campaigns');
            }}
          />
        </>
      )}

      {/* Campaigns List */}
      {(!selectedCampaign || activeTab === 'campaigns') && (
        <>
          {isLoading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : campaigns.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Heart className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">No campaigns yet</h3>
            <p className="text-slate-500 mb-4">Create your first donation campaign to get started</p>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {campaigns.map((campaign) => {
            const progress = campaign.target_amount > 0 
              ? Math.round((campaign.raised_amount / campaign.target_amount) * 100) 
              : 0;
            
            return (
              <Card key={campaign.id} className="overflow-hidden hover:shadow-md transition-shadow">
                <CardContent className="p-0">
                  {/* Campaign Header */}
                  <div className="p-4 border-b bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {campaign.logo ? (
                          <img src={campaign.logo} alt="" className="w-12 h-12 rounded-lg object-cover" />
                        ) : (
                          <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Heart className="h-6 w-6 text-emerald-600" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-slate-900">{campaign.name}</h3>
                          <p className="text-xs text-slate-500">/{campaign.slug}</p>
                        </div>
                      </div>
                      <Badge variant={campaign.is_published ? 'default' : 'secondary'}>
                        {campaign.is_published ? 'Published' : 'Draft'}
                      </Badge>
                      <Badge 
                        variant="outline" 
                        className={campaign.visibility === 'private' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}
                        data-testid={`visibility-badge-${campaign.id}`}
                      >
                        {campaign.visibility === 'private' ? 'Private' : 'Public'}
                      </Badge>
                    </div>
                  </div>

                  {/* Campaign Stats */}
                  <div className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4 text-center">
                      <div>
                        <p className="text-lg font-bold text-emerald-600">${(campaign.raised_amount || 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Raised</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-slate-700">${(campaign.target_amount || 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Goal</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-purple-600">{campaign.overall_progress || 0}%</p>
                        <p className="text-xs text-slate-500">Progress</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex justify-between text-xs mb-1">
                        <span>Funding Progress</span>
                        <span>{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openEditDialog(campaign)}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Quick Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => navigate(`/admin/campaigns/${campaign.id}/edit`)}
                        className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Page Editor
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => navigate(`/admin/campaigns/${campaign.id}/edit?tab=recurrence`)}
                        className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                        data-testid={`campaign-schedule-btn-${campaign.id}`}
                      >
                        <Repeat className="h-3 w-3 mr-1" />
                        {campaign.recurrence?.rule && campaign.recurrence.rule !== 'one_time'
                          ? `Every ${campaign.recurrence.rule === 'interval_days' ? (campaign.recurrence.interval_days || 30) + 'd' : campaign.recurrence.rule}`
                          : 'Schedule'}
                      </Button>
                      <a 
                        href={`${window.location.origin}/campaigns/${campaign.slug}${!campaign.is_published ? '?preview=true' : ''}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline" size="sm">
                          <Eye className="h-3 w-3 mr-1" />
                          {campaign.is_published ? 'View' : 'Preview'}
                        </Button>
                      </a>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setSelectedCampaign(campaign);
                          setActiveTab('pledges');
                        }}
                        className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                        data-testid={`pledges-btn-${campaign.id}`}
                      >
                        <CreditCard className="h-3 w-3 mr-1" />
                        Pledges
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setSelectedCampaign(campaign);
                          setActiveTab('targets');
                        }}
                        className="bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100"
                        data-testid={`targets-btn-${campaign.id}`}
                      >
                        <Calendar className="h-3 w-3 mr-1" />
                        Targets
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openCloneDialog(campaign)}
                        className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                        data-testid={`clone-campaign-${campaign.id}`}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Clone
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleExport(campaign)}
                        className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Export
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setQRCampaign(campaign);
                          setShowQRDialog(true);
                        }}
                        className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                        data-testid={`qr-campaign-${campaign.id}`}
                      >
                        <QrCode className="h-3 w-3 mr-1" />
                        QR
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedCampaign(campaign);
                          setActiveTab('settings');
                        }}
                        className="bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                        data-testid={`settings-campaign-${campaign.id}`}
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Settings
                      </Button>
                      <Button
                        variant={campaign.is_published ? "secondary" : "default"}
                        size="sm"
                        data-testid={`publish-campaign-${campaign.id}`}
                        disabled={togglePublishMutation.isPending}
                        onClick={() => togglePublishMutation.mutate({ 
                          id: campaign.id, 
                          is_published: !campaign.is_published 
                        })}
                      >
                        {togglePublishMutation.isPending ? 'Updating...' : (campaign.is_published ? 'Unpublish' : 'Publish')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`toggle-visibility-${campaign.id}`}
                        disabled={toggleVisibilityMutation.isPending}
                        className={campaign.visibility === 'private' ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100' : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'}
                        onClick={() => toggleVisibilityMutation.mutate({ 
                          id: campaign.id, 
                          visibility: campaign.visibility === 'private' ? 'public' : 'private'
                        })}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        {campaign.visibility === 'private' ? 'Make Public' : 'Make Private'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedCampaign(campaign);
                          setActiveTab('reach');
                        }}
                        className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                        data-testid={`reach-btn-${campaign.id}`}
                      >
                        <Users2 className="h-3 w-3 mr-1" />
                        Reach
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          if (confirm('Delete this campaign?')) {
                            deleteMutation.mutate(campaign.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="contact">Contact & Media</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 pt-4">
                <div>
                  <Label>Campaign Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        name: e.target.value,
                        slug: !editingCampaign ? generateSlug(e.target.value) : formData.slug
                      });
                    }}
                    placeholder="e.g., Hope Community Hospital"
                    required
                  />
                </div>

                <div>
                  <Label>URL Slug *</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">/campaigns/</span>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder="hope-hospital"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Tagline</Label>
                  <Input
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    placeholder="e.g., Building Healthcare for Rural Communities"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fundraising Goal</Label>
                    <Input
                      type="number"
                      value={formData.target_amount}
                      onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                      placeholder="500000"
                    />
                  </div>
                  <div>
                    <Label>Currency (primary)</Label>
                    <CurrencySelect
                      value={formData.currency}
                      showHomeHint={false}
                      testId="campaign-currency-input"
                      onChange={(c) => setFormData({ ...formData, currency: c })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Accepted currencies <span className="text-xs text-slate-400 font-normal">(donors pick from these)</span></Label>
                  <div className="flex flex-wrap items-center gap-2 mt-1 mb-2" data-testid="campaign-allowed-currencies">
                    {(formData.allowed_currencies || '').split(',').map((c) => c.trim()).filter(Boolean).map((code) => (
                      <Badge key={code} variant="secondary" className="gap-1"
                             data-testid={`allowed-currency-${code}`}>
                        {code}
                        <button type="button" className="ml-0.5 text-slate-500 hover:text-red-600"
                                data-testid={`remove-currency-${code}`}
                                onClick={() => setFormData({
                                  ...formData,
                                  allowed_currencies: (formData.allowed_currencies || '').split(',')
                                    .map((x) => x.trim()).filter((x) => x && x !== code).join(','),
                                })}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <CurrencySelect
                    value=""
                    placeholder="Add a currency"
                    showHomeHint={false}
                    testId="campaign-allowed-currencies-input"
                    onChange={(c) => setFormData({
                      ...formData,
                      allowed_currencies: [...new Set([
                        ...(formData.allowed_currencies || '').split(',').map((x) => x.trim()).filter(Boolean),
                        c,
                      ])].join(','),
                    })}
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty to accept the primary currency only. Donors see a selector on the donate page when more than one is set.</p>
                </div>
              </TabsContent>

              <TabsContent value="details" className="space-y-4 pt-4">
                <div>
                  <Label>Mission Statement</Label>
                  <Textarea
                    value={formData.mission_statement}
                    onChange={(e) => setFormData({ ...formData, mission_statement: e.target.value })}
                    placeholder="Describe your mission and purpose..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed description of the project..."
                    rows={4}
                  />
                </div>
              </TabsContent>

              <TabsContent value="contact" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="info@example.org"
                    />
                  </div>
                  <div>
                    <Label>Contact Phone</Label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="+263 77 123 4567"
                    />
                  </div>
                </div>

                <div>
                  <Label>Address</Label>
                  <Input
                    value={formData.contact_address}
                    onChange={(e) => setFormData({ ...formData, contact_address: e.target.value })}
                    placeholder="Location, Country"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <MediaUpload value={formData.hero_image || ''} onChange={(url) => setFormData({ ...formData, hero_image: url })} purpose="hero" label="Hero Image" testId="campaign-hero-upload" previewClassName="h-20" />
                  <MediaUpload value={formData.logo || ''} onChange={(url) => setFormData({ ...formData, logo: url })} purpose="logo" label="Logo" testId="campaign-logo-upload" previewClassName="h-16" />
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saveMutation.isPending}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  editingCampaign ? 'Update Campaign' : 'Create Campaign'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Create from Template
            </DialogTitle>
          </DialogHeader>
          
          {!selectedTemplate ? (
            <div className="py-4">
              <p className="text-slate-600 mb-4">Choose a template to get started quickly with pre-configured milestones and settings.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {templates.map((template) => {
                  const Icon = TEMPLATE_ICONS[template.id] || FileText;
                  const colorClass = TEMPLATE_COLORS[template.id] || 'bg-slate-100 text-slate-700';
                  return (
                    <Card 
                      key={template.id}
                      className={`cursor-pointer hover:shadow-md transition-all border-2 hover:border-purple-300 ${selectedTemplate?.id === template.id ? 'border-purple-500' : ''}`}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setTemplateFormData({
                          name: '',
                          slug: '',
                          goal_amount: template.default_goal || ''
                        });
                      }}
                      data-testid={`template-${template.id}`}
                    >
                      <CardContent className="p-4 text-center">
                        <div className={`w-12 h-12 rounded-lg ${colorClass} flex items-center justify-center mx-auto mb-3`}>
                          <Icon className="h-6 w-6" />
                        </div>
                        <h3 className="font-semibold text-sm mb-1">{template.name}</h3>
                        <p className="text-xs text-slate-500 mb-2">{template.description}</p>
                        <div className="flex items-center justify-center gap-2 text-xs">
                          <Badge variant="secondary">{template.milestones_count} milestones</Badge>
                          {template.default_goal > 0 && (
                            <Badge variant="outline">${(template.default_goal / 1000).toFixed(0)}k goal</Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ) : (
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!templateFormData.name || !templateFormData.slug) {
                toast.error('Name and slug are required');
                return;
              }
              createFromTemplateMutation.mutate({
                templateId: selectedTemplate.id,
                name: templateFormData.name,
                slug: templateFormData.slug,
                goal_amount: templateFormData.goal_amount || null
              });
            }}>
              <div className="py-4 space-y-4">
                <div className={`p-4 rounded-lg border-2 ${TEMPLATE_COLORS[selectedTemplate.id]}`}>
                  <div className="flex items-center gap-3">
                    {React.createElement(TEMPLATE_ICONS[selectedTemplate.id] || FileText, { className: "h-8 w-8" })}
                    <div>
                      <h4 className="font-semibold">{selectedTemplate.name}</h4>
                      <p className="text-sm opacity-80">{selectedTemplate.milestones_count} pre-configured milestones</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="template-name">Campaign Name *</Label>
                  <Input
                    id="template-name"
                    value={templateFormData.name}
                    onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                    placeholder="e.g., Community Health Center Project"
                    data-testid="template-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="template-slug">URL Slug *</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">/campaigns/</span>
                    <Input
                      id="template-slug"
                      value={templateFormData.slug}
                      onChange={(e) => setTemplateFormData({ 
                        ...templateFormData, 
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
                      })}
                      placeholder="community-health-center"
                      data-testid="template-slug-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="template-goal">Goal Amount (optional)</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">$</span>
                    <Input
                      id="template-goal"
                      type="number"
                      value={templateFormData.goal_amount}
                      onChange={(e) => setTemplateFormData({ ...templateFormData, goal_amount: e.target.value })}
                      placeholder={selectedTemplate.default_goal?.toString() || "100000"}
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    Milestone targets will be scaled proportionally if you change the goal.
                  </p>
                </div>
              </div>
              
              <DialogFooter className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setSelectedTemplate(null)}
                >
                  Back to Templates
                </Button>
                <Button 
                  type="submit" 
                  disabled={createFromTemplateMutation.isPending}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="create-from-template-btn"
                >
                  {createFromTemplateMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Create Campaign
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Clone Campaign Dialog */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-purple-600" />
              Clone Campaign
            </DialogTitle>
          </DialogHeader>
          
          {cloningCampaign && (
            <form onSubmit={handleCloneSubmit}>
              <div className="py-4 space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Cloning from:</p>
                  <p className="font-medium">{cloningCampaign.name}</p>
                  <p className="text-xs text-slate-400">/{cloningCampaign.slug}</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="clone-name">New Campaign Name *</Label>
                  <Input
                    id="clone-name"
                    value={cloneFormData.name}
                    onChange={(e) => setCloneFormData({ ...cloneFormData, name: e.target.value })}
                    placeholder="My New Campaign"
                    data-testid="clone-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="clone-slug">URL Slug *</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">/campaigns/</span>
                    <Input
                      id="clone-slug"
                      value={cloneFormData.slug}
                      onChange={(e) => setCloneFormData({ 
                        ...cloneFormData, 
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
                      })}
                      placeholder="my-campaign"
                      data-testid="clone-slug-input"
                    />
                  </div>
                </div>

                <div className="p-3 bg-purple-50 rounded-lg text-sm">
                  <p className="font-medium text-purple-800 mb-1">What will be cloned:</p>
                  <ul className="text-purple-700 text-xs space-y-1">
                    <li>• All campaign settings and branding</li>
                    <li>• Milestones (reset to 0% progress)</li>
                    <li>• Gallery images</li>
                    <li>• Payment configurations</li>
                  </ul>
                  <p className="mt-2 text-purple-600 text-xs">
                    The new campaign will be created as a draft.
                  </p>
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCloneDialog(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={cloneMutation.isPending}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="clone-submit-btn"
                >
                  {cloneMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Cloning...
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Clone Campaign
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-indigo-600" />
              Campaign QR Code
            </DialogTitle>
          </DialogHeader>
          
          {qrCampaign && (
            <div className="py-4 space-y-4">
              <div className="text-center">
                <h3 className="font-semibold text-lg mb-1">{qrCampaign.name}</h3>
                <p className="text-sm text-slate-500 mb-4">Scan to visit campaign page</p>
                
                <div 
                  ref={qrRef}
                  className="bg-white p-6 rounded-lg inline-block border-2 border-slate-100"
                >
                  <QRCodeSVG 
                    value={`${window.location.origin}/campaigns/${qrCampaign.slug}`}
                    size={200}
                    level="H"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor="#000000"
                  />
                </div>
                
                <p className="text-xs text-slate-400 mt-2">
                  {window.location.origin}/campaigns/{qrCampaign.slug}
                </p>
              </div>
              
              <div className="flex gap-2 justify-center flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const svg = qrRef.current?.querySelector('svg');
                    if (!svg) return;
                    
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const data = new XMLSerializer().serializeToString(svg);
                    const img = new window.Image();
                    
                    canvas.width = 400;
                    canvas.height = 400;
                    
                    img.onload = () => {
                      ctx.fillStyle = 'white';
                      ctx.fillRect(0, 0, canvas.width, canvas.height);
                      ctx.drawImage(img, 0, 0, 400, 400);
                      
                      const link = document.createElement('a');
                      link.download = `campaign-qr-${qrCampaign.slug}.png`;
                      link.href = canvas.toDataURL('image/png');
                      link.click();
                      toast.success('QR code downloaded!');
                    };
                    
                    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(data)));
                  }}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download PNG
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const url = `${window.location.origin}/campaigns/${qrCampaign.slug}`;
                    navigator.clipboard.writeText(url);
                    toast.success('Campaign URL copied!');
                  }}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy URL
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    if (!printWindow) {
                      toast.error('Please allow popups to print');
                      return;
                    }
                    
                    const svg = qrRef.current?.querySelector('svg');
                    if (!svg) return;
                    
                    const svgData = new XMLSerializer().serializeToString(svg);
                    
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <title>QR Code - ${qrCampaign.name}</title>
                          <style>
                            body {
                              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                              display: flex;
                              flex-direction: column;
                              align-items: center;
                              justify-content: center;
                              min-height: 100vh;
                              margin: 0;
                              padding: 20px;
                            }
                            .card {
                              border: 2px solid #e5e7eb;
                              border-radius: 16px;
                              padding: 32px;
                              text-align: center;
                              max-width: 400px;
                            }
                            .title { font-size: 18px; font-weight: bold; margin-bottom: 8px; }
                            .subtitle { font-size: 14px; color: #6b7280; margin-bottom: 16px; }
                            .qr-container { margin: 24px 0; }
                            .url { font-size: 12px; color: #9ca3af; word-break: break-all; }
                          </style>
                        </head>
                        <body>
                          <div class="card">
                            <div class="title">${qrCampaign.name}</div>
                            <div class="subtitle">Scan to support our campaign</div>
                            <div class="qr-container">${svgData}</div>
                            <div class="url">${window.location.origin}/campaigns/${qrCampaign.slug}</div>
                          </div>
                          <script>window.onload = () => { window.print(); window.close(); }</script>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                  }}
                >
                  Print
                </Button>
              </div>
              
              <div className="p-3 bg-indigo-50 rounded-lg text-sm text-indigo-800">
                <p className="font-medium mb-1">💡 Tips:</p>
                <ul className="text-xs text-indigo-700 space-y-1">
                  <li>• Print and display at events, offices, or churches</li>
                  <li>• Share on social media to increase donations</li>
                  <li>• Add to promotional materials and flyers</li>
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== READINESS CHECKLIST ==============
// Tiny wrapper that loads /school/workflow/campaign/{tid}/{cid}/readiness and
// renders the shared WorkflowChecklist. The settings tab is the natural home
// because operators land there once they're trying to launch the campaign.
const CampaignReadinessChecklist = ({ campaignId, tenantId }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['campaign-readiness', tenantId, campaignId],
    queryFn: () => api.get(`/school/workflow/campaign/${tenantId}/${campaignId}/readiness`).then((r) => r.data),
    enabled: !!campaignId,
    refetchOnWindowFocus: true,
  });

  const publish = useMutation({
    mutationFn: () => api.post(`/school/workflow/campaign/${tenantId}/${campaignId}/publish`),
    onSuccess: () => {
      toast.success('Campaign published.');
      qc.invalidateQueries({ queryKey: ['campaign-readiness'] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e) => {
      const det = e?.response?.data?.detail;
      if (typeof det === 'object' && det?.blockers?.length) {
        toast.error(det.blockers[0]);
      } else {
        toast.error(typeof det === 'string' ? det : 'Cannot publish yet');
      }
    },
  });

  return (
    <div className="mb-4">
      <WorkflowChecklist
        data={data}
        loading={isLoading}
        onPublish={() => publish.mutate()}
        publishing={publish.isPending}
        publishLabel="Publish campaign"
      />
    </div>
  );
};

export default CampaignsManagement;
